// Admin: print template detail editor.
//
// Tabs:
//   - Метаданные: name / description / brand / page_width_mm / page_height_mm
//                 + archive / restore / clone / rotate / orient
//   - SVG фон:    upload + show current background as preview
//   - Плейсхолдеры: extract from SVG → table editor (key, label, type,
//                   x/y/width/height) → save (regenerates Typst source)
//
// Visual DnD canvas + preview UI are deferred to a follow-up session.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import {
  adminMetricsApi,
  manufacturingOrdersApi,
  printAdminApi,
  printApi,
  type PrintTemplatePlaceholder,
  type PrintTemplateVariablesSchema,
} from "@/lib/api";
import { useSystemSettings } from "@/lib/system";

type TabKey = "meta" | "svg" | "placeholders" | "preview";
type PhView = "visual" | "table";

// Дефолтный цвет данных на шаблоне — как на бэкенде
// (backend-ts/src/print/templates/placeholder_color.ts): у паспортов цвет из
// профиля компании, у остальных чёрный. Вид шаблона — свободная строка
// (passport, passport-8040, qt-passport), поэтому проверяем вхождение подстроки.
const DEFAULT_PLACEHOLDER_COLOR = "#000000";

function usePlaceholderColor(kind: string): string {
  const passportColor = useSystemSettings().data?.company.passport_text_color ?? DEFAULT_PLACEHOLDER_COLOR;
  return kind.toLowerCase().includes("passport") ? passportColor : DEFAULT_PLACEHOLDER_COLOR;
}

// Роли, которым можно выдать доступ к шаблону. ADMIN не в списке — он видит
// и правит всё всегда (а также любой держатель print.template.manage).
const TEMPLATE_ACCESS_ROLES: [string, string][] = [
  ["SALES_OFFICE", "Офис продаж (ОЕ)"],
  ["DOC_OPERATOR", "Оформитель документов"],
  ["PRODUCTION_MANAGER", "Начальник производства"],
  ["SHIFT_LEAD", "Мастер смены"],
  ["OPERATOR", "Оператор"],
  ["QC_INSPECTOR", "Контролёр ОТК"],
];

export default function PrintTemplateDetail() {
  return (
    <RequirePermission codes="print.template.manage">
      <Inner />
    </RequirePermission>
  );
}

function Inner() {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<TabKey>("meta");

  const tplQ = useQuery({
    queryKey: ["print", "template", id],
    queryFn: () => printApi.getTemplate(id!),
    enabled: !!id,
  });

  if (tplQ.isLoading) return <LoadingState />;
  if (tplQ.isError) return <ErrorState error={tplQ.error} />;
  if (!tplQ.data) return null;
  const t = tplQ.data;

  return (
    <>
      <PageHeader
        eyebrow="админ · шаблон"
        title={t.name}
        description={`${t.brand} · ${t.kind} · ${t.page_width_mm}×${t.page_height_mm} мм · v${t.version}${t.is_archived ? " · АРХИВ" : ""}`}
        actions={
          <Link to="/admin/print-templates" className="btn">
            ← К списку
          </Link>
        }
      />

      <nav className="flex items-center gap-1 border-b border-border mb-5">
        {[
          { k: "meta" as const, label: "Метаданные" },
          { k: "svg" as const, label: "SVG-фон" },
          { k: "placeholders" as const, label: "Плейсхолдеры" },
          { k: "preview" as const, label: "Превью" },
        ].map(({ k, label }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={
              "px-4 py-2 text-sm border-b-2 -mb-px transition-colors " +
              (tab === k ? "border-accent text-text" : "border-transparent text-text-muted hover:text-text")
            }
          >
            {label}
          </button>
        ))}
      </nav>

      {tab === "meta" && <MetaTab tpl={t} />}
      {tab === "svg" && <SvgTab tpl={t} />}
      {tab === "placeholders" && <PlaceholdersTab tpl={t} />}
      {tab === "preview" && <PreviewTab tpl={t} />}
    </>
  );
}

// ===================================================================
// Tab 1 — Metadata + lifecycle actions (archive / restore / clone / rotate / orient).
// ===================================================================

function MetaTab({
  tpl,
}: {
  tpl: NonNullable<ReturnType<typeof printApi.getTemplate> extends Promise<infer R> ? R : never>;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: tpl.name,
    description: tpl.description ?? "",
    brand: tpl.brand,
    page_width_mm: String(tpl.page_width_mm),
    page_height_mm: String(tpl.page_height_mm),
    default_agent_id: tpl.default_agent_id ?? "",
    default_printer_name: tpl.default_printer_name ?? "",
  });
  const [allowedRoles, setAllowedRoles] = useState<string[]>(tpl.allowed_roles ?? []);
  const [err, setErr] = useState<string | null>(null);

  // Default-target picker shows ВСЕХ онлайн-агентов: kind шаблона —
  // не routing-критерий, оператор может настроить любую пару
  // агент+принтер как дефолт (например для рекламной печати на
  // обычном лазернике вне зависимости от того что declared принтером).
  const agentsQ = useQuery({
    queryKey: ["print", "agents", "online"],
    queryFn: () => printApi.listOnlineAgents(),
  });
  const selectedDefaultAgent =
    agentsQ.data?.items.find((a) => a.id === form.default_agent_id) ?? null;

  const save = useMutation({
    mutationFn: () =>
      printAdminApi.update(tpl.id, {
        name: form.name.trim(),
        description: form.description.trim() || null,
        brand: form.brand.trim(),
        page_width_mm: Number(form.page_width_mm),
        page_height_mm: Number(form.page_height_mm),
        // null clears the default; empty-string in the dropdown maps to null.
        default_agent_id: form.default_agent_id || null,
        default_printer_name: form.default_printer_name || null,
        // Пустой список → только админ (бэк сохранит null).
        allowed_roles: allowedRoles,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["print", "template", tpl.id] });
      void qc.invalidateQueries({ queryKey: ["print", "templates"] });
    },
    onError: (e) => setErr(e instanceof Error ? e.message : "Ошибка"),
  });

  const archive = useMutation({
    mutationFn: () => (tpl.is_archived ? printAdminApi.restore(tpl.id) : printAdminApi.archive(tpl.id)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["print", "template", tpl.id] });
      void qc.invalidateQueries({ queryKey: ["print", "templates"] });
    },
  });

  const clone = useMutation({
    mutationFn: () => printAdminApi.clone(tpl.id),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["print", "templates"] });
      navigate(`/admin/print-templates/${r.id}`);
    },
  });

  const rotate = useMutation({
    mutationFn: () => printAdminApi.rotate(tpl.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["print", "template", tpl.id] }),
  });

  const orientSwap = useMutation({
    mutationFn: () => printAdminApi.orient(tpl.id, "swap"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["print", "template", tpl.id] }),
  });

  const remove = useMutation({
    mutationFn: () => printAdminApi.remove(tpl.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["print", "templates"] });
      navigate("/admin/print-templates");
    },
    onError: (e) => setErr(e instanceof Error ? e.message : "Не удалось удалить"),
  });

  return (
    <section className="space-y-6">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          save.mutate();
        }}
        className="card p-5 space-y-3"
      >
        <h3 className="text-base">Метаданные</h3>
        <Field label="Название">
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
          />
        </Field>
        <Field label="Описание">
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
            rows={2}
          />
        </Field>
        <Field label="Brand">
          <input
            type="text"
            value={form.brand}
            onChange={(e) => setForm({ ...form, brand: e.target.value })}
            className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Ширина, мм">
            <input
              type="number"
              step="any"
              value={form.page_width_mm}
              onChange={(e) => setForm({ ...form, page_width_mm: e.target.value })}
              className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm font-mono tabular-nums"
            />
          </Field>
          <Field label="Высота, мм">
            <input
              type="number"
              step="any"
              value={form.page_height_mm}
              onChange={(e) => setForm({ ...form, page_height_mm: e.target.value })}
              className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm font-mono tabular-nums"
            />
          </Field>
        </div>

        <div className="pt-2 border-t border-border/50 space-y-3">
          <p className="text-text-muted text-xs">
            <strong className="text-text">Дефолтное место печати.</strong> Оператор в форме
            «Печать вручную» получит эти значения как pre-selected, но сможет переопределить.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Агент (по умолчанию)">
              <select
                value={form.default_agent_id}
                onChange={(e) =>
                  setForm({
                    ...form,
                    default_agent_id: e.target.value,
                    default_printer_name: "",
                  })
                }
                className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
              >
                <option value="">— не задан (оператор выбирает «авто») —</option>
                {(agentsQ.data?.items ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Принтер агента (по умолчанию)">
              <select
                value={form.default_printer_name}
                onChange={(e) =>
                  setForm({ ...form, default_printer_name: e.target.value })
                }
                className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
                disabled={!selectedDefaultAgent}
              >
                <option value="">— не задан (агент выбирает сам) —</option>
                {(selectedDefaultAgent?.printers ?? []).map((p) => (
                  <option key={p.id} value={p.windows_name || p.name}>
                    {p.windows_name ? `${p.name} · ${p.windows_name}` : p.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {!agentsQ.isLoading && (agentsQ.data?.items.length ?? 0) === 0 && (
            <p className="text-text-muted text-xs">
              Сейчас нет онлайн-агентов. Дефолт можно задать позже, когда хотя бы
              один агент подключится и пришлёт свои принтеры.
            </p>
          )}
        </div>

        <div className="border-t border-border pt-4">
          <h4 className="text-sm font-medium">Доступ по ролям</h4>
          <p className="text-text-muted text-xs mt-1">
            Кому шаблон доступен для печати и редактирования. Администратор видит
            и правит все шаблоны всегда. Если не выбрана ни одна роль — шаблон
            доступен только администраторам (служебный).
          </p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {TEMPLATE_ACCESS_ROLES.map(([role, label]) => (
              <label key={role} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={allowedRoles.includes(role)}
                  onChange={(e) =>
                    setAllowedRoles((cur) =>
                      e.target.checked
                        ? [...cur, role]
                        : cur.filter((r) => r !== role),
                    )
                  }
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="submit" className="btn btn-primary" disabled={save.isPending}>
            {save.isPending ? "Сохранение…" : "Сохранить"}
          </button>
        </div>
      </form>

      <div className="card p-5 space-y-3">
        <h3 className="text-base">Действия</h3>
        <p className="text-text-muted text-sm">
          Клонирование создаёт независимую копию (kind/brand/SVG/плейсхолдеры). Поворот меняет
          местами стороны страницы и переразмещает плейсхолдеры. Swap — только меняет стороны,
          плейсхолдеры остаются где были (используйте, если SVG уже правильной ориентации, но
          размеры в БД перепутаны).
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            className="btn"
            onClick={() => clone.mutate()}
            disabled={clone.isPending}
          >
            ⎘ Клонировать
          </button>
          <button
            className="btn"
            onClick={() => rotate.mutate()}
            disabled={rotate.isPending}
          >
            ⟳ Повернуть 90°
          </button>
          <button
            className="btn"
            onClick={() => orientSwap.mutate()}
            disabled={orientSwap.isPending}
          >
            ⇆ Swap (только размеры)
          </button>
          <button
            className={tpl.is_archived ? "btn btn-primary" : "btn"}
            onClick={() => archive.mutate()}
            disabled={archive.isPending}
          >
            {tpl.is_archived ? "↻ Восстановить" : "📦 Архивировать"}
          </button>
        </div>

        <div className="pt-4 mt-2 border-t border-state-danger/30">
          <p className="text-text-muted text-xs mb-2">
            Безвозвратное удаление: сам шаблон + SVG-фон + Typst-источник. Заблокировано,
            если на шаблон ссылаются печатные задачи (тогда сначала архивируйте).
          </p>
          <button
            className="btn btn-danger"
            onClick={() => {
              setErr(null);
              const ok = window.confirm(
                `Удалить шаблон «${tpl.name}» безвозвратно? Действие нельзя отменить.`,
              );
              if (ok) remove.mutate();
            }}
            disabled={remove.isPending}
          >
            {remove.isPending ? "Удаление…" : "🗑 Удалить шаблон"}
          </button>
        </div>
      </div>
    </section>
  );
}

// ===================================================================
// Tab 2 — SVG background upload + preview.
// ===================================================================

/** Fetches the SVG-background as TEXT.  We embed it inline via
 *  dangerouslySetInnerHTML rather than referencing via <object> or
 *  SVG <image href=blob:…> — both of those have rendering issues
 *  (zero size when the SVG root lacks width/height attrs, plus blob
 *  URLs in nested SVG <image> are flaky cross-context).  Inline gives
 *  us guaranteed correct sizing via CSS and full DOM access for the
 *  visual editor overlay. */
function useBackgroundSvgText(templateId: string, hasBg: boolean): string | null {
  const blobQ = useQuery({
    queryKey: ["print", "template", templateId, "bg-svg"],
    queryFn: async () => {
      const blob = await printAdminApi.fetchBackground(templateId);
      return blob.text();
    },
    enabled: hasBg,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
  return blobQ.data ?? null;
}

/** Strip the outer <svg …>…</svg> wrapper so the inner shapes can be
 *  embedded inside another SVG element (the editor canvas) at our own
 *  viewBox.  Returns the inner markup, or null if parsing fails. */
function extractSvgInner(svgText: string | null): string | null {
  if (!svgText) return null;
  // Greedy match between the first opening <svg …> and the LAST </svg>.
  const m = svgText.match(/<svg[\s\S]*?>([\s\S]*)<\/svg\s*>\s*$/i);
  return m ? m[1] ?? null : null;
}

/** Extract the uploaded SVG's viewBox attribute — uploaded files often
 *  use pixel coordinate systems (e.g. "0 0 1000 700") that don't match
 *  our canvas's mm-based viewBox.  When embedded as a nested <svg>, the
 *  caller passes this string so the browser scales the inner shapes to
 *  fit the page rectangle. */
function extractSvgViewBox(svgText: string | null): string | null {
  if (!svgText) return null;
  const m = svgText.match(/<svg\b[^>]*\bviewBox\s*=\s*"([^"]+)"/i);
  return m ? m[1] ?? null : null;
}

function SvgTab({
  tpl,
}: {
  tpl: NonNullable<ReturnType<typeof printApi.getTemplate> extends Promise<infer R> ? R : never>;
}) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const hasBg = !!tpl.background_svg_path;
  const svgText = useBackgroundSvgText(tpl.id, hasBg);

  const [info, setInfo] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: () => {
      if (!file) throw new Error("Файл не выбран");
      return printAdminApi.uploadBackground(tpl.id, file);
    },
    onSuccess: (res) => {
      setFile(null);
      setErr(null);
      // Сообщить оператору, что мы вычистили {{...}} из фона и расставили
      // плейсхолдеры за него — иначе он не поймёт, куда делись надписи
      // и откуда взялись боксы во вкладке «Плейсхолдеры».
      setInfo(
        res.auto_extracted > 0
          ? `SVG-фон загружен. Плейсхолдеров автоматически извлечено: ${res.auto_extracted}. ` +
              `Перейдите во вкладку «Плейсхолдеры» — боксы уже стоят на своих местах. ` +
              `В превью маркеры {{...}} не рендерятся (вычищаются на лету при рендере).`
          : "SVG-фон загружен. Маркеров {{...}} в фоне не найдено — расставьте плейсхолдеры вручную во вкладке «Плейсхолдеры».",
      );
      void qc.invalidateQueries({ queryKey: ["print", "template", tpl.id] });
      void qc.invalidateQueries({ queryKey: ["print", "template", tpl.id, "bg-svg"] });
    },
    onError: (e) => {
      setInfo(null);
      setErr(e instanceof Error ? e.message : "Ошибка");
    },
  });

  const removeBg = useMutation({
    mutationFn: () => printAdminApi.removeBackground(tpl.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["print", "template", tpl.id] });
      void qc.invalidateQueries({ queryKey: ["print", "template", tpl.id, "bg-svg"] });
    },
    onError: (e) => setErr(e instanceof Error ? e.message : "Не удалось удалить фон"),
  });

  // Render the SVG inline at the page's mm-aspect — force width/height
  // via wrapper container so external <svg> attributes don't matter.
  const previewW = Math.min(tpl.page_width_mm * 2, 800);
  const previewH = previewW * (tpl.page_height_mm / tpl.page_width_mm);

  return (
    <section className="space-y-4">
      <div className="card p-5 space-y-3">
        <h3 className="text-base">SVG-фон</h3>
        <p className="text-text-muted text-sm">
          Загрузите векторный фон. Плейсхолдеры расставляются в редакторе на следующей вкладке —
          в самом SVG никаких <code className="font-mono bg-surface px-1">{"{{var_name}}"}</code>{" "}
          не нужно.
        </p>
        <div className="flex items-center gap-3">
          <input
            type="file"
            accept="image/svg+xml,.svg"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="text-sm"
          />
          <button
            className="btn btn-primary"
            onClick={() => {
              setErr(null);
              upload.mutate();
            }}
            disabled={!file || upload.isPending}
          >
            {upload.isPending ? "Загрузка…" : "Загрузить"}
          </button>
          {hasBg && (
            <button
              className="btn"
              onClick={() => {
                setErr(null);
                const ok = window.confirm(
                  "Удалить текущий SVG-фон? Плейсхолдеры и переменные остаются, можно загрузить новый файл.",
                );
                if (ok) removeBg.mutate();
              }}
              disabled={removeBg.isPending}
              title="Открепить фон. Плейсхолдеры/переменные не трогаются."
            >
              {removeBg.isPending ? "Удаление…" : "🗑 Удалить фон"}
            </button>
          )}
        </div>
        {info && (
          <div className="p-3 rounded-md border border-accent/40 bg-accent/5 text-sm flex items-start gap-3">
            <span aria-hidden className="text-accent">ℹ</span>
            <p className="flex-1">{info}</p>
            <button
              type="button"
              className="text-text-muted hover:text-text text-xs"
              onClick={() => setInfo(null)}
              aria-label="Закрыть"
            >
              ✕
            </button>
          </div>
        )}
        {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}
      </div>

      <div className="card p-5">
        <h3 className="text-base mb-3">Текущий фон</h3>
        {!hasBg && <p className="text-text-muted text-sm">SVG-фон ещё не загружен.</p>}
        {hasBg && !svgText && <p className="text-text-muted text-sm">Загрузка фона…</p>}
        {hasBg && svgText && (
          <div
            className="bg-white border border-border inline-block p-2 [&_svg]:block [&_svg]:w-full [&_svg]:h-full"
            style={{ width: `${previewW + 16}px`, height: `${previewH + 16}px` }}
            dangerouslySetInnerHTML={{ __html: svgText }}
          />
        )}
      </div>
    </section>
  );
}

// ===================================================================
// Tab 3 — Placeholders table editor.
// ===================================================================

type EditablePlaceholder = PrintTemplatePlaceholder & {
  // Bonus column the editor surfaces — taken from variables_schema by key.
  _label?: string;
  _type?: "text" | "number" | "date" | "enum" | "auto_increment";
  _required?: boolean;
  // auto_increment-only fields, surfaced inline when the type is set
  // accordingly.  Plugin's variablesSchemaSchema demands these on
  // discriminated union match.
  _prefix?: string;
  _padWidth?: number;
  _startAt?: number;
  _editableByOperator?: boolean;
  // enum-only.
  _enumOptions?: string[];
};

// Plugin's placeholderSchema.key + variableSchema.key require snake_case.
// We re-implement the same regex client-side so the operator sees a
// meaningful inline message instead of a server-side 422.
const KEY_PATTERN = /^[a-z_][a-z0-9_]*$/;

/** Гарнитура нового плейсхолдера — самая частая среди уже существующих, а не
 *  жёсткий "Inter".
 *
 *  Шаблон свёрстан одной гарнитурой (паспорта — Montserrat), и новый бокс со
 *  схемным дефолтом визуально выпадает из вёрстки. Ошибка тихая: значение
 *  печатается, просто другим шрифтом, и замечают это уже на бумаге. */
function dominantFont(rows: ReadonlyArray<{ font?: string }>): string {
  const tally = new Map<string, number>();
  for (const r of rows) {
    if (!r.font) continue;
    tally.set(r.font, (tally.get(r.font) ?? 0) + 1);
  }
  let best = "Inter";
  let bestN = 0;
  for (const [font, n] of tally) {
    if (n > bestN) {
      best = font;
      bestN = n;
    }
  }
  return best;
}

function PlaceholdersTab({
  tpl,
}: {
  tpl: NonNullable<ReturnType<typeof printApi.getTemplate> extends Promise<infer R> ? R : never>;
}) {
  const qc = useQueryClient();
  const phColor = usePlaceholderColor(tpl.kind);
  // Hydrate initial state from the template row.  variables_schema has
  // the variable metadata; placeholders has the layout.  We pair them
  // by `key` so the table shows one row per variable.
  const initialVars = useMemo(
    () => extractVariables(tpl.variables_schema),
    [tpl.variables_schema],
  );
  const initialPhs = useMemo(
    () => extractPlaceholders(tpl.placeholders, phColor),
    [phColor, tpl.placeholders],
  );

  const [rows, setRows] = useState<EditablePlaceholder[]>(() =>
    mergeRows(initialPhs, initialVars),
  );
  useEffect(() => {
    setRows(mergeRows(initialPhs, initialVars));
  }, [initialPhs, initialVars]);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const extract = useMutation({
    mutationFn: () => printAdminApi.extractPlaceholders(tpl.id),
    onSuccess: (draft) => {
      setErr(null);
      // Если фон вообще не содержит маркеров `{{key}}` / `{KEY}` в <text>
      // нодах (типичная ситуация: текст векторизован в <path>) — не
      // затираем уже расставленные плейсхолдеры и явно объясняем оператору
      // что это нормально и где их расставлять.
      if (draft.placeholders.length === 0) {
        setInfo(
          "В SVG-фоне не найдено ни одного маркера {{key}} или {KEY} в <text>-нодах. " +
            "Чаще всего это значит, что текст в файле сконвертирован в кривые (<path>) — " +
            "извлекать из него нечего. Это нормально: расставьте плейсхолдеры визуально " +
            "поверх фона (кнопка «+ Добавить» и перетаскивание боксов).",
        );
        return;
      }
      setInfo(
        `Извлечено плейсхолдеров: ${draft.placeholders.length}. Текущая таблица заменена ` +
          `черновиком — отредактируйте и нажмите «Сохранить».`,
      );
      const drafted = mergeRows(
        draft.placeholders.map((p) => ({ ...p })),
        draft.variables.map((v) => ({ ...v })),
      );
      setRows(drafted);
    },
    onError: (e) => {
      setInfo(null);
      setErr(e instanceof Error ? e.message : "Ошибка");
    },
  });

  // Compute validation issues once per render — surface inline.
  // Errors block save; warnings inform but don't block.
  const issues = useMemo(() => validateRows(rows), [rows]);
  const hasErrors = issues.some((i) => i.severity === "error");

  // Variables schema demands ONE row per variable key.  Placeholders can
  // reference the same key multiple times (e.g. серийный номер на четырёх
  // местах паспорта).  So we dedupe variables by key, keeping the first
  // metadata that appeared.  Placeholders all go through verbatim.
  const save = useMutation({
    mutationFn: () => {
      const firstError = issues.find((i) => i.severity === "error");
      if (firstError) throw new Error(firstError.message);
      const placeholders: PrintTemplatePlaceholder[] = rows.map((r) => ({
        id: r.id,
        key: r.key,
        x: r.x,
        y: r.y,
        width: r.width,
        height: r.height,
        font: r.font ?? dominantFont(rows),
        fontSize: r.fontSize ?? 10,
        // Plugin schema requires literal 400 — no other weight supported yet.
        fontWeight: 400,
        align: r.align ?? "left",
        color: r.color ?? phColor,
        // Pass-through подписи/единицы: не теряем prefix/suffix при сохранении.
        ...(typeof r.prefix === "string" && r.prefix !== ""
          ? { prefix: r.prefix }
          : {}),
        ...(typeof r.suffix === "string" && r.suffix !== ""
          ? { suffix: r.suffix }
          : {}),
        ...(r.hideWhenEmpty === true ? { hideWhenEmpty: true } : {}),
        ...(typeof r.source === "string" && r.source.trim() !== "" ? { source: r.source.trim() } : {}),
      }));
      const seen = new Set<string>();
      const variables: PrintTemplateVariablesSchema["variables"] = [];
      for (const r of rows) {
        if (seen.has(r.key)) continue;
        seen.add(r.key);
        const t = r._type ?? "text";
        const base = {
          key: r.key,
          label: r._label || r.key,
          type: t,
          required: r._required ?? false,
        };
        if (t === "auto_increment") {
          variables.push({
            ...base,
            ...(r._prefix !== undefined ? { prefix: r._prefix } : {}),
            padWidth: r._padWidth ?? 4,
            startAt: r._startAt ?? 1,
            editableByOperator: r._editableByOperator ?? false,
          });
        } else if (t === "enum") {
          variables.push({
            ...base,
            enumOptions: r._enumOptions?.length ? r._enumOptions : ["—"],
          });
        } else {
          variables.push(base);
        }
      }
      const variables_schema: PrintTemplateVariablesSchema = { variables };
      return printAdminApi.updatePlaceholders(tpl.id, { placeholders, variables_schema });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["print", "template", tpl.id] });
    },
    onError: (e) => setErr(e instanceof Error ? e.message : "Ошибка"),
  });

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        id: cryptoRandomId(),
        key: `var${prev.length + 1}`,
        x: 10,
        y: 10,
        width: 40,
        height: 8,
        // Не "Inter": берём гарнитуру шаблона, иначе новый бокс печатается
        // другим шрифтом, чем всё остальное на странице.
        font: dominantFont(prev),
        fontSize: 10,
        fontWeight: 400,
        align: "left",
        color: phColor,
        _label: "",
        _type: "text",
        _required: false,
      },
    ]);
  };

  const removeRow = (idx: number) =>
    setRows((prev) => prev.filter((_, i) => i !== idx));

  // Per-placeholder fields = geometry (x/y/width/height/font, etc).
  // Per-VARIABLE fields = type/label/required + auto_increment params +
  // enum options.  When the operator edits a variable-level field on
  // one row, every row that shares the same `key` must mirror the
  // change — otherwise validateRows reports a "type расходится" error
  // and silently blocks the Save button.  Geometry is left alone so
  // each visual position can be tuned independently.
  const VARIABLE_FIELDS: Array<keyof EditablePlaceholder> = [
    "_type",
    "_label",
    "_required",
    "_prefix",
    "_padWidth",
    "_startAt",
    "_editableByOperator",
    "_enumOptions",
  ];

  const updateRow = (idx: number, patch: Partial<EditablePlaceholder>) =>
    setRows((prev) => {
      const target = prev[idx];
      if (!target) return prev;
      const variablePatch: Partial<EditablePlaceholder> = {};
      const geometryPatch: Partial<EditablePlaceholder> = {};
      for (const [k, v] of Object.entries(patch) as Array<
        [keyof EditablePlaceholder, EditablePlaceholder[keyof EditablePlaceholder]]
      >) {
        if (VARIABLE_FIELDS.includes(k)) {
          (variablePatch as Record<string, unknown>)[k] = v;
        } else {
          (geometryPatch as Record<string, unknown>)[k] = v;
        }
      }
      // `key` is special: variable-level, but also identifies which
      // rows mirror.  Changing it means renaming the variable — apply
      // to every row that currently shares the OLD key.
      const keyRename =
        patch.key !== undefined && patch.key !== target.key
          ? { oldKey: target.key, newKey: patch.key }
          : null;

      return prev.map((r, i) => {
        if (i === idx) return { ...r, ...patch };
        if (keyRename && r.key === keyRename.oldKey) {
          return { ...r, key: keyRename.newKey, ...variablePatch };
        }
        if (!keyRename && r.key === target.key && Object.keys(variablePatch).length > 0) {
          return { ...r, ...variablePatch };
        }
        return r;
      });
    });

  const [view, setView] = useState<PhView>("visual");

  return (
    <section className="space-y-4">
      <div className="card p-5">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h3 className="text-base">Плейсхолдеры и переменные</h3>
          <div className="flex items-center gap-2">
            <ViewToggle value={view} onChange={setView} />
            <button
              className="btn"
              onClick={() => extract.mutate()}
              disabled={extract.isPending || !tpl.background_svg_path}
              title={
                !tpl.background_svg_path
                  ? "Сначала загрузите SVG-фон"
                  : "Ищет маркеры {{key}} или {KEY} в <text>-нодах SVG. " +
                    "Если текст в файле сконвертирован в кривые (<path>) — " +
                    "извлекать нечего, расставляйте плейсхолдеры вручную."
              }
            >
              {extract.isPending ? "Извлечение…" : "🔍 Извлечь из SVG"}
            </button>
            <button className="btn" onClick={addRow}>
              + Добавить
            </button>
          </div>
        </div>
        <p className="text-text-muted text-xs mb-3">
          Координаты <code className="font-mono">x</code>, <code className="font-mono">y</code> —
          от левого верхнего угла страницы в мм. Поле «type» определяет UI ввода для оператора в
          ручной печати. Несколько строк с одним <code className="font-mono">key</code> — это{" "}
          <strong>нормально</strong>: одна переменная отрисовывается в нескольких местах.
        </p>

        {issues.length > 0 && (
          <div
            className={
              "mb-3 p-3 rounded-md border " +
              (hasErrors
                ? "border-state-danger/40 bg-state-danger/5"
                : "border-state-warn/40 bg-state-warn/5")
            }
          >
            <p
              className={
                "font-mono text-xs uppercase tracking-wider mb-1 " +
                (hasErrors ? "text-state-danger" : "text-state-warn")
              }
            >
              {hasErrors ? "Перед сохранением исправьте" : "Предупреждения"}
            </p>
            <ul className="text-sm space-y-0.5">
              {issues.map((i) => (
                <li key={i.id}>
                  <span className="font-mono text-xs text-text-muted">row {i.rowIndex + 1}:</span>{" "}
                  {i.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        {view === "visual" && (
          <VisualEditor
            tpl={tpl}
            rows={rows}
            setRows={setRows}
            updateRow={updateRow}
            removeRow={removeRow}
          />
        )}

        {view === "table" && rows.length === 0 && (
          <p className="text-text-muted text-sm">Список пуст. Извлеките из SVG или добавьте вручную.</p>
        )}

        {view === "table" && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-muted text-xs font-mono uppercase border-b border-border">
                  <th className="text-left py-2 pr-2">key</th>
                  <th className="text-left py-2 pr-2">label</th>
                  <th className="text-left py-2 pr-2">type</th>
                  <th className="text-right py-2 pr-2">x</th>
                  <th className="text-right py-2 pr-2">y</th>
                  <th className="text-right py-2 pr-2">w</th>
                  <th className="text-right py-2 pr-2">h</th>
                  {/* Колонка правит fontSize, а называлась «font» — читалось
                      как гарнитура. Гарнитура теперь своей колонкой рядом. */}
                  <th className="text-right py-2 pr-2">размер</th>
                  <th className="text-left py-2 pr-2">гарнитура</th>
                  <th className="text-left py-2 pr-2">источник</th>
                  <th className="text-left py-2 pr-2">подпись</th>
                  <th className="text-left py-2 pr-2">единица</th>
                  <th className="text-center py-2 pr-2">скрывать</th>
                  <th className="text-left py-2 pr-2">цвет</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <Fragment key={r.id}>
                    <tr className={r._type === "auto_increment" ? "" : "border-b border-border/40"}>
                      <td className="py-1 pr-2">
                        <input
                          className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm w-32 font-mono text-xs"
                          value={r.key}
                          onChange={(e) => updateRow(idx, { key: e.target.value })}
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <input
                          className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm w-40"
                          value={r._label ?? ""}
                          onChange={(e) => updateRow(idx, { _label: e.target.value })}
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <select
                          className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
                          value={r._type ?? "text"}
                          onChange={(e) =>
                            updateRow(idx, { _type: e.target.value as EditablePlaceholder["_type"] })
                          }
                        >
                          <option value="text">text</option>
                          <option value="number">number</option>
                          <option value="date">date</option>
                          <option value="enum">enum</option>
                          <option value="auto_increment">auto_increment</option>
                        </select>
                      </td>
                      <td className="py-1 pr-2">
                        <NumberCell value={r.x} onChange={(v) => updateRow(idx, { x: v })} />
                      </td>
                      <td className="py-1 pr-2">
                        <NumberCell value={r.y} onChange={(v) => updateRow(idx, { y: v })} />
                      </td>
                      <td className="py-1 pr-2">
                        <NumberCell value={r.width} onChange={(v) => updateRow(idx, { width: v })} />
                      </td>
                      <td className="py-1 pr-2">
                        <NumberCell value={r.height} onChange={(v) => updateRow(idx, { height: v })} />
                      </td>
                      <td className="py-1 pr-2">
                        <NumberCell
                          value={r.fontSize ?? 10}
                          onChange={(v) => updateRow(idx, { fontSize: v })}
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <input
                          className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm w-28"
                          value={r.font ?? ""}
                          placeholder={dominantFont(rows)}
                          onChange={(e) => updateRow(idx, { font: e.target.value })}
                          aria-label={`Гарнитура ${r.key}`}
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <SourceInput
                          value={r.source ?? ""}
                          onChange={(v) => updateRow(idx, { source: v })}
                          disabled={r._type === "auto_increment"}
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <input
                          className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm w-40"
                          value={r.prefix ?? ""}
                          placeholder="напр. Минимальный поток –"
                          onChange={(e) => updateRow(idx, { prefix: e.target.value })}
                          aria-label={`Подпись ${r.key}`}
                          disabled={r._type === "auto_increment"}
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <input
                          className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm w-24"
                          value={r.suffix ?? ""}
                          placeholder="напр. %"
                          onChange={(e) => updateRow(idx, { suffix: e.target.value })}
                          aria-label={`Единица ${r.key}`}
                          disabled={r._type === "auto_increment"}
                        />
                      </td>
                      <td className="py-1 pr-2 text-center">
                        <input
                          type="checkbox"
                          checked={r.hideWhenEmpty === true}
                          onChange={(e) => updateRow(idx, { hideWhenEmpty: e.target.checked })}
                          aria-label={`Скрывать пустое ${r.key}`}
                          disabled={r._type === "auto_increment"}
                        />
                      </td>
                      <td className="py-1 pr-2">
                        <input
                          type="color"
                          value={r.color ?? phColor}
                          onChange={(e) => updateRow(idx, { color: e.target.value.toUpperCase() })}
                          className="h-9 w-14 rounded-md bg-surface border border-border p-1"
                          aria-label={`Цвет ${r.key}`}
                        />
                      </td>
                      <td className="py-1 pl-2 text-right">
                        <button
                          type="button"
                          className="btn"
                          onClick={() => removeRow(idx)}
                          aria-label="Удалить"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                    {r._type === "auto_increment" && (
                      <tr className="border-b border-border/40">
                        <td colSpan={14} className="pt-1 pb-3 pl-4 pr-2">
                          <AutoIncrementSettings
                            row={r}
                            onChange={(patch) => updateRow(idx, patch)}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {info && (
          <div className="mt-3 p-3 rounded-md border border-accent/40 bg-accent/5 text-sm flex items-start gap-3">
            <span aria-hidden className="text-accent">ℹ</span>
            <p className="flex-1">{info}</p>
            <button
              type="button"
              className="text-text-muted hover:text-text text-xs"
              onClick={() => setInfo(null)}
              aria-label="Закрыть"
            >
              ✕
            </button>
          </div>
        )}

        {err && (
          <div className="mt-3">
            <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            className="btn btn-primary"
            onClick={() => {
              setErr(null);
              save.mutate();
            }}
            disabled={save.isPending || hasErrors}
          >
            {save.isPending ? "Сохранение…" : "Сохранить переменные"}
          </button>
        </div>
        <p className="text-text-muted text-xs mt-2">
          После сохранения регенерируется Typst-источник. Превью PDF — в соседней вкладке
          «Превью» (доступна сразу после сохранения).
        </p>
      </div>

      <p className="text-text-muted text-xs">
        <Link to="/print" className="underline">
          Перейти к ручной печати →
        </Link>
      </p>
    </section>
  );
}

/**
 * Inline settings panel for an auto_increment-typed variable.
 *
 * Conceptually the variable produces:    prefix + pad0(startAt + i, padWidth)
 *
 * So `prefix=ECT-`, `startAt=42`, `padWidth=4` over a job with total=3
 * renders "ECT-0042", "ECT-0043", "ECT-0044".  padWidth=0 means no
 * leading zeros (just the decimal value).
 *
 * `editableByOperator=true` exposes a seed text-field on the operator
 * print form so they can override the admin-baked numbers per-job
 * (e.g. typing "ECT-0150" re-derives prefix/startAt/padWidth from
 * that seed string via parseSerialSeed on the server).
 *
 * Without this panel the variable saves with undefined prefix/startAt
 * and padWidth, which fails server-side schema validation and silently
 * leaves the placeholder un-substituted in the PDF.
 */
function AutoIncrementSettings({
  row,
  onChange,
}: {
  row: EditablePlaceholder;
  onChange: (patch: Partial<EditablePlaceholder>) => void;
}) {
  return (
    <div className="rounded-md border border-accent/30 bg-accent/5 p-3 space-y-3">
      <p className="text-xs font-mono uppercase tracking-wider text-accent">
        Параметры auto_increment
      </p>
      <p className="text-text-muted text-xs">
        Серийный номер строится как{" "}
        <code className="font-mono">prefix + pad₀(startAt + i, padWidth)</code>. При тираже{" "}
        <code className="font-mono">N</code> печатаются последовательные значения{" "}
        <code className="font-mono">i = 0..N-1</code>.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Префикс" hint='Напр. "ECT-" — то, что встанет перед числом.'>
          <input
            type="text"
            className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm font-mono"
            value={row._prefix ?? ""}
            onChange={(e) => onChange({ _prefix: e.target.value })}
            placeholder="(нет)"
          />
        </Field>
        <Field label="Начать с (startAt)" hint="Число, с которого начнётся отсчёт.">
          <NumberCell
            value={row._startAt ?? 1}
            onChange={(v) => onChange({ _startAt: Math.max(0, Math.floor(v)) })}
          />
        </Field>
        <Field
          label="Ширина числа (padWidth)"
          hint="Сколько цифр в числе. 4 → 0001; 0 → без ведущих нулей."
        >
          <NumberCell
            value={row._padWidth ?? 4}
            onChange={(v) => onChange({ _padWidth: Math.max(0, Math.floor(v)) })}
          />
        </Field>
      </div>
      <label className="flex items-start gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={row._editableByOperator === true}
          onChange={(e) => onChange({ _editableByOperator: e.target.checked })}
          className="mt-1"
        />
        <span>
          Оператор может задать стартовый серийник вручную
          <span className="block text-text-muted text-xs">
            На форме печати появится текстовое поле; вводимое там значение (напр.{" "}
            <code className="font-mono">ECT-0150</code>) подменит prefix/startAt/padWidth для
            этого задания.
          </span>
        </span>
      </label>
      <p className="text-xs text-text-muted">
        Пример с текущими настройками:{" "}
        <span className="font-mono">
          {previewAutoInc(row._prefix ?? "", row._startAt ?? 1, row._padWidth ?? 4, 0)}
        </span>
        {" → "}
        <span className="font-mono">
          {previewAutoInc(row._prefix ?? "", row._startAt ?? 1, row._padWidth ?? 4, 1)}
        </span>
        {" → "}
        <span className="font-mono">
          {previewAutoInc(row._prefix ?? "", row._startAt ?? 1, row._padWidth ?? 4, 2)}
        </span>
        {" → …"}
      </p>
    </div>
  );
}

function previewAutoInc(prefix: string, startAt: number, padWidth: number, index: number): string {
  const padded = String(startAt + index).padStart(Math.max(0, padWidth), "0");
  return prefix ? `${prefix}${padded}` : padded;
}

const CORE_SOURCES = ["order.article", "order.code", "order.customer_code", "order.date", "item.serial", "item.code"];

/** Поле источника с подсказками: поля заказа и изделия, атрибуты заказов
 *  техпроцессов, показатели изделия.  Без прав на справочники — только ядро. */
function SourceInput({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const processes = useQuery({
    queryKey: ["manufacturing-orders", "processes"],
    queryFn: () => manufacturingOrdersApi.processes(),
    staleTime: 60_000,
    retry: false,
  });
  const metrics = useQuery({
    queryKey: ["admin", "metrics", "all"],
    queryFn: () => adminMetricsApi.list(),
    staleTime: 60_000,
    retry: false,
  });
  const options = useMemo(() => {
    const attrs = (processes.data ?? []).flatMap((p) => p.order.attributes.map((a) => `attr.${a.key}`));
    const ms = (metrics.data?.items ?? []).map((m) => `metric.${m.key}`);
    return [...new Set([...CORE_SOURCES, ...attrs, ...ms])];
  }, [processes.data, metrics.data]);
  const listId = useId();
  return (
    <>
      <input
        className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm font-mono text-xs w-40"
        list={listId}
        value={value}
        placeholder="напр. order.article"
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
      <datalist id={listId}>
        {options.map((o) => (
          <option key={o} value={o} />
        ))}
      </datalist>
    </>
  );
}

function NumberCell({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <input
      type="number"
      step="any"
      className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm w-20 font-mono tabular-nums text-right"
      value={Number.isFinite(value) ? value : ""}
      onChange={(e) => onChange(Number(e.target.value))}
    />
  );
}

function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-text-muted text-xs mt-1">{hint}</p>}
    </label>
  );
}

// ===================================================================
// View toggle pill: visual ↔ table.
// ===================================================================

function ViewToggle({
  value,
  onChange,
}: {
  value: PhView;
  onChange: (v: PhView) => void;
}) {
  return (
    <div className="inline-flex items-center rounded-md border border-border overflow-hidden">
      {(["visual", "table"] as const).map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={
            "px-3 py-1.5 text-xs font-mono uppercase tracking-wider " +
            (value === v ? "bg-accent/20 text-text" : "text-text-muted hover:text-text")
          }
        >
          {v === "visual" ? "Визуально" : "Таблица"}
        </button>
      ))}
    </div>
  );
}

// ===================================================================
// VisualEditor — DnD + resize boxes over the SVG background.
//
// Implementation notes:
//   - Single SVG with viewBox in millimeters (matches the rendered page).
//   - Background <image> wraps the same /print/templates/:id/background
//     URL the SvgTab uses.
//   - Each placeholder is a <g> with a translucent <rect> + key label.
//     Pointer-down on the body → drag (move x/y).  Pointer-down on the
//     bottom-right handle → resize (move width/height).
//   - Coordinate conversion screen→SVG uses `getScreenCTM().inverse()`.
//   - Selection is local to the editor; clicking the canvas background
//     deselects.  Side panel on the right edits the selected box.
// ===================================================================

type DragMode = "move" | "resize-se" | null;

interface DragState {
  mode: DragMode;
  rowId: string;
  // SVG-space coords at pointer-down + initial row geometry.
  startSvgX: number;
  startSvgY: number;
  startRowX: number;
  startRowY: number;
  startRowW: number;
  startRowH: number;
}

function VisualEditor({
  tpl,
  rows,
  setRows,
  updateRow,
  removeRow,
}: {
  tpl: NonNullable<ReturnType<typeof printApi.getTemplate> extends Promise<infer R> ? R : never>;
  rows: EditablePlaceholder[];
  setRows: React.Dispatch<React.SetStateAction<EditablePlaceholder[]>>;
  updateRow: (idx: number, patch: Partial<EditablePlaceholder>) => void;
  removeRow: (idx: number) => void;
}) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const phColor = usePlaceholderColor(tpl.kind);

  const hasBg = !!tpl.background_svg_path;
  // Inline the SVG markup directly into our editor canvas.  Wrap in a
  // nested <svg> so the uploaded artwork is scaled from its own viewBox
  // to fit the mm-based page rectangle.
  const svgText = useBackgroundSvgText(tpl.id, hasBg);
  const svgInner = useMemo(() => extractSvgInner(svgText), [svgText]);
  const innerViewBox = useMemo(() => extractSvgViewBox(svgText), [svgText]);

  // Convert a pointer event into SVG-space (= page mm).
  const toSvg = useCallback((evt: React.PointerEvent | PointerEvent): { x: number; y: number } | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const m = svg.getScreenCTM();
    if (!m) return null;
    const inv = m.inverse();
    const p = pt.matrixTransform(inv);
    return { x: p.x, y: p.y };
  }, []);

  const idToIndex = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((r, i) => m.set(r.id, i));
    return m;
  }, [rows]);

  const startDrag = (
    e: React.PointerEvent<SVGElement>,
    row: EditablePlaceholder,
    mode: Exclude<DragMode, null>,
  ) => {
    const pt = toSvg(e);
    if (!pt) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = {
      mode,
      rowId: row.id,
      startSvgX: pt.x,
      startSvgY: pt.y,
      startRowX: row.x,
      startRowY: row.y,
      startRowW: row.width,
      startRowH: row.height,
    };
    setSelectedId(row.id);
    e.stopPropagation();
  };

  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const pt = toSvg(e);
    if (!pt) return;
    const dx = pt.x - d.startSvgX;
    const dy = pt.y - d.startSvgY;
    const idx = idToIndex.get(d.rowId);
    if (idx === undefined) return;
    if (d.mode === "move") {
      updateRow(idx, {
        x: roundMm(Math.max(0, d.startRowX + dx)),
        y: roundMm(Math.max(0, d.startRowY + dy)),
      });
    } else if (d.mode === "resize-se") {
      updateRow(idx, {
        width: roundMm(Math.max(2, d.startRowW + dx)),
        height: roundMm(Math.max(2, d.startRowH + dy)),
      });
    }
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  // Click on empty canvas deselects.
  const onCanvasPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (e.target === svgRef.current) setSelectedId(null);
  };

  const selected = selectedId ? rows.find((r) => r.id === selectedId) ?? null : null;

  // Pixel size of the canvas — bound by aspect ratio of the template.
  // 800×CSS px max; scale page dims down proportionally.
  const aspect = tpl.page_width_mm / tpl.page_height_mm;
  const maxW = 900;
  const maxH = 700;
  const pxW = aspect >= maxW / maxH ? maxW : maxH * aspect;
  const pxH = aspect >= maxW / maxH ? maxW / aspect : maxH;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[auto_320px] gap-4">
      <div className="card p-3 bg-white inline-block overflow-auto">
        {!hasBg && (
          <p className="text-text-muted text-sm p-4">
            SVG-фон ещё не загружен. Перейдите на вкладку «SVG-фон» и загрузите файл.
          </p>
        )}
        {hasBg && (
          <svg
            ref={svgRef}
            viewBox={`0 0 ${tpl.page_width_mm} ${tpl.page_height_mm}`}
            width={pxW}
            height={pxH}
            style={{ touchAction: "none", userSelect: "none", background: "white" }}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onPointerDown={onCanvasPointerDown}
          >
            {/* Uploaded SVG embedded as a nested <svg>.  Its own
                 viewBox is preserved so any pixel/inch coordinate
                 system inside gets uniformly scaled to fit the
                 page rectangle (preserveAspectRatio=none → exact
                 stretch).  Inline inner markup lets pointer events
                 still hit our overlay <g>s on top.                  */}
            <svg
              x={0}
              y={0}
              width={tpl.page_width_mm}
              height={tpl.page_height_mm}
              viewBox={innerViewBox ?? `0 0 ${tpl.page_width_mm} ${tpl.page_height_mm}`}
              preserveAspectRatio="none"
              pointerEvents="none"
              {...(svgInner ? { dangerouslySetInnerHTML: { __html: svgInner } } : {})}
            />
            {rows.map((r) => {
              const isSel = r.id === selectedId;
              return (
                <g
                  key={r.id}
                  transform={`translate(${r.x},${r.y})`}
                  style={{ cursor: "move" }}
                  onPointerDown={(e) => startDrag(e, r, "move")}
                >
                  <rect
                    x={0}
                    y={0}
                    width={r.width}
                    height={r.height}
                    fill={isSel ? "rgba(0,180,255,0.18)" : "rgba(0,180,255,0.06)"}
                    stroke={isSel ? "rgb(0,180,255)" : "rgba(0,180,255,0.55)"}
                    strokeWidth={0.25}
                  />
                  {/* Tiny label inside the box.  Use SVG-mm units so the
                       text scales with the canvas. */}
                  <text
                    x={0.6}
                    y={Math.max(2.5, (r.fontSize ?? 10) * 0.4)}
                    fontSize={Math.max(2.5, Math.min(r.height * 0.6, 3.2))}
                    fill="rgb(40,40,40)"
                    fontFamily="ui-monospace, monospace"
                    pointerEvents="none"
                  >
                    {r.key}
                  </text>
                  {/* Resize handle in the SE corner — only when selected. */}
                  {isSel && (
                    <rect
                      x={r.width - 1.5}
                      y={r.height - 1.5}
                      width={1.5}
                      height={1.5}
                      fill="rgb(0,180,255)"
                      style={{ cursor: "nwse-resize" }}
                      onPointerDown={(e) => startDrag(e, r, "resize-se")}
                    />
                  )}
                </g>
              );
            })}
          </svg>
        )}
      </div>

      <aside className="card p-4 space-y-3 h-fit">
        {selected ? (
          <SelectedPanel
            row={selected}
            idx={idToIndex.get(selected.id) ?? 0}
            fallbackColor={phColor}
            update={updateRow}
            remove={removeRow}
            onDuplicate={() => {
              setRows((prev) => [
                ...prev,
                {
                  ...selected,
                  id: cryptoRandomId(),
                  x: roundMm(selected.x + 5),
                  y: roundMm(selected.y + 5),
                },
              ]);
            }}
          />
        ) : (
          <p className="text-text-muted text-sm">
            Кликните по плейсхолдеру на холсте, чтобы редактировать. Перетаскивайте за тело,
            растягивайте за уголок справа-снизу.
          </p>
        )}
      </aside>
    </div>
  );
}

function SelectedPanel({
  row,
  idx,
  fallbackColor,
  update,
  remove,
  onDuplicate,
}: {
  row: EditablePlaceholder;
  idx: number;
  fallbackColor: string;
  update: (idx: number, patch: Partial<EditablePlaceholder>) => void;
  remove: (idx: number) => void;
  onDuplicate: () => void;
}) {
  return (
    <>
      <h3 className="text-base">Свойства</h3>
      <Field label="key" hint="snake_case; одна переменная может быть в нескольких позициях">
        <input
          className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm font-mono"
          value={row.key}
          onChange={(e) => update(idx, { key: e.target.value })}
        />
      </Field>
      <Field label="label">
        <input
          className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
          value={row._label ?? ""}
          onChange={(e) => update(idx, { _label: e.target.value })}
        />
      </Field>
      <Field label="type">
        <select
          className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
          value={row._type ?? "text"}
          onChange={(e) => update(idx, { _type: e.target.value as EditablePlaceholder["_type"] })}
        >
          <option value="text">text</option>
          <option value="number">number</option>
          <option value="date">date</option>
          <option value="enum">enum</option>
          <option value="auto_increment">auto_increment</option>
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="x, мм">
          <NumberCell value={row.x} onChange={(v) => update(idx, { x: v })} />
        </Field>
        <Field label="y, мм">
          <NumberCell value={row.y} onChange={(v) => update(idx, { y: v })} />
        </Field>
        <Field label="ширина">
          <NumberCell value={row.width} onChange={(v) => update(idx, { width: v })} />
        </Field>
        <Field label="высота">
          <NumberCell value={row.height} onChange={(v) => update(idx, { height: v })} />
        </Field>
        <Field label="font size">
          <NumberCell
            value={row.fontSize ?? 10}
            onChange={(v) => update(idx, { fontSize: v })}
          />
        </Field>
        <Field label="align">
          <select
            className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
            value={row.align ?? "left"}
            onChange={(e) =>
              update(idx, { align: e.target.value as PrintTemplatePlaceholder["align"] })
            }
          >
            <option value="left">left</option>
            <option value="center">center</option>
            <option value="right">right</option>
          </select>
        </Field>
        <Field label="цвет текста">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={row.color ?? fallbackColor}
              onChange={(e) => update(idx, { color: e.target.value.toUpperCase() })}
              className="h-9 w-14 rounded-md bg-surface border border-border p-1"
              aria-label={`Цвет ${row.key}`}
            />
            <code className="text-xs font-mono text-text-muted">
              {row.color ?? fallbackColor}
            </code>
          </div>
        </Field>
      </div>
      {row._type !== "auto_increment" && (
        <>
          <Field
            label="источник данных"
            hint="Откуда значение берётся при печати по изделию или заказу. Пусто — значение вводят руками или передаёт вызывающий."
          >
            <SourceInput value={row.source ?? ""} onChange={(v) => update(idx, { source: v })} />
          </Field>
          <Field
            label="подпись перед значением"
            hint="Печатается в том же боксе тем же шрифтом, напр. «Минимальный поток –». Значение остаётся чистым числом."
          >
            <input
              className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
              value={row.prefix ?? ""}
              placeholder="напр. Минимальный поток –"
              onChange={(e) => update(idx, { prefix: e.target.value })}
            />
          </Field>
          <Field
            label="единица после значения"
            hint="Напр. «м³/сут», «%». Часть шаблона, а не вводимых данных — не ломает раскладку."
          >
            <input
              className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
              value={row.suffix ?? ""}
              placeholder="напр. м³/сут"
              onChange={(e) => update(idx, { suffix: e.target.value })}
            />
          </Field>
          <Field
            label="пустое значение"
            hint="Скрывается весь блок — вместе с подписью и единицей. Иначе на документ выйдет «Минимальный поток –  м³/сут» без числа. Ноль пустотой НЕ считается и печатается."
          >
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={row.hideWhenEmpty === true}
                onChange={(e) => update(idx, { hideWhenEmpty: e.target.checked })}
              />
              не печатать, если не заполнено
            </label>
          </Field>
        </>
      )}
      {row._type === "auto_increment" && (
        <AutoIncrementSettings row={row} onChange={(patch) => update(idx, patch)} />
      )}
      <div className="flex flex-wrap gap-2 pt-2">
        <button className="btn" onClick={onDuplicate}>
          ⎘ Дублировать
        </button>
        <button className="btn btn-danger" onClick={() => remove(idx)}>
          ✕ Удалить
        </button>
      </div>
    </>
  );
}

function roundMm(v: number): number {
  // 0.1 mm precision is more than enough for label layout and keeps the
  // sidebar inputs readable.
  return Math.round(v * 10) / 10;
}

// ===================================================================
// PreviewTab — mock-values form + iframe with the rendered PDF blob.
// Reads the saved variables_schema; doesn't share state with editor.
// ===================================================================

function PreviewTab({
  tpl,
}: {
  tpl: NonNullable<ReturnType<typeof printApi.getTemplate> extends Promise<infer R> ? R : never>;
}) {
  const variables = useMemo(() => extractVariables(tpl.variables_schema), [tpl.variables_schema]);
  const [values, setValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const v of variables) init[v.key] = "";
    return init;
  });
  useEffect(() => {
    setValues((prev) => {
      const next: Record<string, string> = {};
      for (const v of variables) next[v.key] = prev[v.key] ?? "";
      return next;
    });
  }, [variables]);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Which auto_increment "page" to preview (0-based, like the real
  // print job's i in 0..total-1).  Plain text/number variables ignore
  // this; auto_increment variables shift by the index value.
  const [index, setIndex] = useState<string>("0");

  const hasAutoIncrement = variables.some((v) => v.type === "auto_increment");

  const run = useMutation({
    mutationFn: () => {
      // Cast everything to string/number — backend's preview accepts both.
      // For auto_increment with editableByOperator the operator types a
      // seed string here; for non-editable auto_increment we send '' so
      // the server falls back to admin-baked prefix/startAt/padWidth.
      const payload: Record<string, string | number> = {};
      for (const v of variables) {
        const raw = values[v.key] ?? "";
        if (raw === "") continue;
        if (v.type === "number") {
          const n = Number(raw);
          if (Number.isFinite(n)) payload[v.key] = n;
        } else {
          payload[v.key] = raw;
        }
      }
      const idxN = Number(index);
      return printAdminApi.preview(
        tpl.id,
        payload,
        Number.isFinite(idxN) && idxN >= 0 ? Math.floor(idxN) : 0,
      );
    },
    onSuccess: (blob) => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl(URL.createObjectURL(blob));
    },
    onError: (e) => setErr(e instanceof Error ? e.message : "Ошибка рендера"),
  });

  // Cleanup blob on unmount.
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
      <div className="card p-5 space-y-3 h-fit">
        <h3 className="text-base">Mock-значения</h3>
        <p className="text-text-muted text-xs">
          Заполните поля и нажмите «Сгенерировать». PDF откроется справа — это серверный рендер
          через текущий Typst-источник шаблона.
        </p>

        {variables.length === 0 ? (
          <p className="text-text-muted text-sm">
            У шаблона нет переменных. Сохраните их во вкладке «Плейсхолдеры», затем вернитесь.
          </p>
        ) : (
          <div className="space-y-2">
            {variables.map((v) => {
              // auto_increment is server-assigned.  An editable variant
              // accepts an optional operator-supplied seed; a non-editable
              // one is fully baked from prefix/startAt/padWidth and has
              // no input at all (just a readout).
              if (v.type === "auto_increment") {
                const editable = v.editableByOperator === true;
                const hint = editable
                  ? "Опционально: введите стартовый серийник (напр. ECT-0150) — он переопределит prefix/startAt/padWidth для этого превью."
                  : `Назначается сервером. Префикс: «${String(v.prefix ?? "")}», ` +
                    `начать с ${String(v.startAt ?? 1)}, ширина числа ${String(v.padWidth ?? 4)}.`;
                return (
                  <Field key={v.key} label={`${v.label} (${v.key})`} hint={hint}>
                    {editable ? (
                      <input
                        type="text"
                        className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm font-mono"
                        value={values[v.key] ?? ""}
                        onChange={(e) => setValues((s) => ({ ...s, [v.key]: e.target.value }))}
                        placeholder="(использовать настройки админа)"
                      />
                    ) : (
                      <div className="text-text-muted text-xs font-mono italic">
                        auto_increment, сервер задаст значение
                      </div>
                    )}
                  </Field>
                );
              }
              return (
                <Field key={v.key} label={`${v.label} (${v.key})`}>
                  {v.type === "enum" && Array.isArray(v.enumOptions) && v.enumOptions.length > 0 ? (
                    <select
                      className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
                      value={values[v.key] ?? ""}
                      onChange={(e) => setValues((s) => ({ ...s, [v.key]: e.target.value }))}
                    >
                      <option value="">— не задано —</option>
                      {v.enumOptions.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={v.type === "date" ? "date" : v.type === "number" ? "number" : "text"}
                      className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
                      value={values[v.key] ?? ""}
                      onChange={(e) => setValues((s) => ({ ...s, [v.key]: e.target.value }))}
                    />
                  )}
                </Field>
              );
            })}
            {hasAutoIncrement && (
              <Field
                label="Страница серии (index)"
                hint="0 = первая метка серии, 1 = вторая и т.д. Для авто-инкремент-переменных смещает значение на index. Текст/число игнорируют."
              >
                <input
                  type="number"
                  min={0}
                  step={1}
                  className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm font-mono tabular-nums"
                  value={index}
                  onChange={(e) => setIndex(e.target.value)}
                />
              </Field>
            )}
          </div>
        )}

        {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}

        <button
          className="btn btn-primary w-full"
          onClick={() => {
            setErr(null);
            run.mutate();
          }}
          disabled={run.isPending}
        >
          {run.isPending ? "Рендер…" : "🖨 Сгенерировать превью"}
        </button>
      </div>

      <div className="card p-3 min-h-[500px]">
        {pdfUrl ? (
          <iframe
            src={pdfUrl}
            title="Превью PDF"
            className="w-full h-[80vh] bg-white"
          />
        ) : (
          <div className="h-full min-h-[500px] flex items-center justify-center text-text-muted text-sm">
            Превью появится после нажатия «Сгенерировать».
          </div>
        )}
      </div>
    </section>
  );
}

// ===================================================================
// Helpers.
// ===================================================================

function extractPlaceholders(
  raw: unknown,
  fallbackColor = DEFAULT_PLACEHOLDER_COLOR,
): PrintTemplatePlaceholder[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((p): p is Record<string, unknown> => !!p && typeof p === "object")
    .map((p) => ({
      id: String(p.id ?? cryptoRandomId()),
      key: String(p.key ?? ""),
      x: Number(p.x ?? 0),
      y: Number(p.y ?? 0),
      width: Number(p.width ?? 40),
      height: Number(p.height ?? 8),
      font: p.font !== undefined ? String(p.font) : "Inter",
      fontSize: p.fontSize !== undefined ? Number(p.fontSize) : 10,
      fontWeight: p.fontWeight !== undefined ? Number(p.fontWeight) : 400,
      align: (p.align as PrintTemplatePlaceholder["align"]) ?? "left",
      color: p.color !== undefined ? String(p.color) : fallbackColor,
      // Подпись (prefix) и единица (suffix) — сохраняем, чтобы правка шаблона в
      // UI их не затирала.  Выставляются в данных шаблона; печатаются рендером.
      ...(p.prefix !== undefined && p.prefix !== null && String(p.prefix) !== ""
        ? { prefix: String(p.prefix) }
        : {}),
      ...(p.suffix !== undefined && p.suffix !== null && String(p.suffix) !== ""
        ? { suffix: String(p.suffix) }
        : {}),
      ...(typeof p.source === "string" && p.source !== "" ? { source: p.source } : {}),
    }))
    .filter((p) => p.key);
}

function extractVariables(raw: unknown): PrintTemplateVariablesSchema["variables"] {
  if (!raw || typeof raw !== "object") return [];
  const obj = raw as { variables?: unknown };
  if (!Array.isArray(obj.variables)) return [];
  return obj.variables
    .filter((v): v is Record<string, unknown> => !!v && typeof v === "object")
    .map((v) => {
      const base = {
        key: String(v.key ?? ""),
        label: String(v.label ?? v.key ?? ""),
        type: (v.type as PrintTemplateVariablesSchema["variables"][number]["type"]) ?? "text",
        required: v.required === true,
      };
      // Hydrate per-type fields so the admin editor doesn't lose settings
      // on reload.  These are stored verbatim in variables_schema and the
      // visual editor's AutoIncrementSettings / enum-options inputs
      // read them back.
      const extras: Record<string, unknown> = {};
      if (base.type === "auto_increment") {
        if (typeof v.prefix === "string") extras.prefix = v.prefix;
        if (typeof v.padWidth === "number") extras.padWidth = v.padWidth;
        if (typeof v.startAt === "number") extras.startAt = v.startAt;
        if (typeof v.editableByOperator === "boolean")
          extras.editableByOperator = v.editableByOperator;
      }
      if (base.type === "enum" && Array.isArray(v.enumOptions)) {
        extras.enumOptions = (v.enumOptions as unknown[]).map((x) => String(x));
      }
      return { ...base, ...extras } as PrintTemplateVariablesSchema["variables"][number];
    })
    .filter((v) => v.key);
}

function mergeRows(
  placeholders: PrintTemplatePlaceholder[],
  variables: PrintTemplateVariablesSchema["variables"],
): EditablePlaceholder[] {
  const byKey = new Map<string, PrintTemplateVariablesSchema["variables"][number]>();
  for (const v of variables) byKey.set(v.key, v);
  return placeholders.map((p) => {
    const v = byKey.get(p.key);
    return {
      ...p,
      _label: v?.label ?? p.key,
      _type: v?.type ?? "text",
      _required: v?.required ?? false,
      ...(v?.type === "auto_increment"
        ? {
            _prefix: typeof v.prefix === "string" ? v.prefix : "",
            _padWidth: typeof v.padWidth === "number" ? v.padWidth : 4,
            _startAt: typeof v.startAt === "number" ? v.startAt : 1,
            _editableByOperator:
              typeof v.editableByOperator === "boolean" ? v.editableByOperator : false,
          }
        : {}),
      ...(v?.type === "enum" && Array.isArray(v.enumOptions)
        ? { _enumOptions: (v.enumOptions as unknown[]).map((x) => String(x)) }
        : {}),
    };
  });
}

function cryptoRandomId(): string {
  // Reasonably-unique short id, fine for in-form bookkeeping.
  return Math.random().toString(36).slice(2, 10);
}

interface RowIssue {
  id: string;
  rowIndex: number;
  message: string;
  severity: "error" | "warn";
}

/** Catch issues that the plugin schemas would reject on the server:
 *   - empty / non-snake_case key
 *   - empty placeholder.id
 *   - duplicate placeholder.id (allowed key duplicates ARE fine — same
 *     variable rendered in multiple locations)
 *   - non-positive width / height (zod's `.positive()`)
 *   - inconsistent variable metadata across rows that share a key
 *     (different type/label/required — only one wins, server takes first)
 *
 *  Returns a flat list of issues so the UI can render and block save. */
function validateRows(rows: EditablePlaceholder[]): RowIssue[] {
  const issues: RowIssue[] = [];
  const seenIds = new Set<string>();
  // Track first-seen variable metadata per key, flag conflicting later rows.
  const firstByKey = new Map<string, EditablePlaceholder>();

  rows.forEach((r, i) => {
    if (!r.key.trim()) {
      issues.push({ id: `key-empty-${i}`, rowIndex: i, severity: "error", message: "key пустой" });
    } else if (!KEY_PATTERN.test(r.key)) {
      issues.push({
        id: `key-bad-${i}`,
        rowIndex: i,
        severity: "error",
        message: `key «${r.key}» должен быть snake_case (a-z, 0-9, _)`,
      });
    }

    if (!r.id || !r.id.trim()) {
      issues.push({
        id: `id-empty-${i}`,
        rowIndex: i,
        severity: "error",
        message: "placeholder id пустой",
      });
    } else if (seenIds.has(r.id)) {
      issues.push({
        id: `id-dup-${i}`,
        rowIndex: i,
        severity: "error",
        message: `повтор placeholder id «${r.id}» (id должен быть уникальным; key — можно повторять)`,
      });
    }
    seenIds.add(r.id);

    if (!(r.width > 0) || !(r.height > 0)) {
      issues.push({
        id: `dim-${i}`,
        rowIndex: i,
        severity: "error",
        message: "ширина и высота должны быть > 0",
      });
    }
    if (r.x < 0 || r.y < 0) {
      issues.push({
        id: `xy-${i}`,
        rowIndex: i,
        severity: "error",
        message: "x и y должны быть ≥ 0",
      });
    }

    const first = firstByKey.get(r.key);
    if (first) {
      if ((first._type ?? "text") !== (r._type ?? "text")) {
        issues.push({
          id: `type-${i}`,
          rowIndex: i,
          severity: "error",
          message: `type «${r._type}» расходится с первой строкой этого key (там «${first._type}»)`,
        });
      }
      if ((first._label || first.key) !== (r._label || r.key)) {
        issues.push({
          id: `label-${i}`,
          rowIndex: i,
          severity: "warn",
          message: `label расходится с первой строкой того же key — сохранится значение первой строки`,
        });
      }
    } else {
      firstByKey.set(r.key, r);
    }
  });

  return issues;
}
